import React, { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';

import {
	atRiskOfBlockingStatuses,
	EmployeeByActivityPoleTrainingStatus,
	validStatuses,
} from '@/constants/EmployeeByActivityPoleConstants';
import { employeeByActivityPoleTrainingColumns } from '@/constants/tableColumns/EmployeeByActivityPoleTrainingColumns';
import { EmployeeByActivityPole } from '@/types/EmployeeByActivityPole';
import { cn } from '@/utils/cn';
import { getFirstAndLastName } from '@/utils/getFirstAndLastName';

import { CardInfo } from '../atoms/CardInfo';
import LightTooltip from '../atoms/LightTooltip';
import Table from '../organisms/Table';

interface EmployeeByActivityPoleCardProps {
	employeeByActivityPole: EmployeeByActivityPole;
	allowed: boolean;
	atRiskOfBlocking?: boolean;
}

export const EmployeeByActivityPoleCard: React.FC<
	EmployeeByActivityPoleCardProps
> = ({ employeeByActivityPole, allowed, atRiskOfBlocking }) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleToggleCard = () => {
		setIsOpen((prev) => !prev);
	};
	const { employee } = employeeByActivityPole;
	const amountOfPendencies = allowed
		? employeeByActivityPole.employeeByActivityPoleCondition.filter((it) =>
				atRiskOfBlockingStatuses.includes(
					it.status as EmployeeByActivityPoleTrainingStatus,
				),
			).length
		: employeeByActivityPole.employeeByActivityPoleCondition.filter(
				(it) =>
					!validStatuses.includes(
						it.status as EmployeeByActivityPoleTrainingStatus,
					),
			).length;

	return (
		<div className="flex min-h-12 w-full gap-2  ">
			<div
				className={cn(
					'flex w-[30rem]  justify-start overflow-hidden rounded-md bg-white p-4 shadow-md',
					isOpen ? 'items-start pt-5' : 'items-center',
				)}
			>
				<button onClick={handleToggleCard}>
					<FaChevronDown
						className={`mr-2 h-3 w-3 transition-transform ${isOpen && 'rotate-180'} text-primary`}
					/>
				</button>
				<div className="flex gap-4 whitespace-nowrap text-sm text-gray-700">
					<LightTooltip title={employee.name} arrow placement="top">
						<div>
							<CardInfo
								label="NOME"
								value={employee.alias || getFirstAndLastName(employee.name)}
							/>
						</div>
					</LightTooltip>
					<CardInfo label="CHAPA" value={employee.employeeNumber} />
				</div>
			</div>
			<div className="max-h-88 flex flex-1 flex-col overflow-hidden rounded-md bg-white shadow-md">
				<div className="flex items-center justify-start p-4">
					<div className="flex gap-4 overflow-x-auto whitespace-nowrap text-sm text-gray-700">
						<CardInfo
							label="ATIVIDADE"
							value={employeeByActivityPole.activityPole.activity.name}
							valueClassName="w-48 lg:w-52"
						/>
						<CardInfo
							label="POLO DA ATIVIDADE"
							value={employeeByActivityPole.activityPole.pole.name}
							valueClassName="w-20 lg:w-24 uppercase"
						/>
						{!allowed || atRiskOfBlocking ? (
							<CardInfo
								label="TOTAL DE PENDÊNCIAS"
								value={amountOfPendencies.toString()}
								valueClassName="size-6 flex items-center justify-center rounded-md border bg-[#d1e6ff] p-1 font-bold text-primary lg:mr-8 lg:size-6"
							/>
						) : (
							<></>
						)}
						<CardInfo label="FUNÇÃO" value={employee.role} />
						<CardInfo
							label="LOCAL DE TRABALHO"
							value={employee.workStation?.name || ''}
							valueClassName="w-24 lg:w-28"
						/>
						<CardInfo label="DIRETORIA" value={employee.board} />
						<CardInfo label="GERÊNCIA" value={employee.management} />
						<CardInfo label="COORDENAÇÃO" value={employee.coordination} />
					</div>
				</div>
				{isOpen && (
					<div className="custom-scrollbar mt-4 min-h-0 w-full flex-1 overflow-x-auto overflow-y-auto bg-white px-4 pb-6">
						<Table
							columns={employeeByActivityPoleTrainingColumns(
								employee.id,
								employeeByActivityPole.allowed,
							)}
							data={employeeByActivityPole.employeeByActivityPoleCondition}
							tBodyClassname="border-none "
							trBodyClassname="border-none"
							tdHeaderClassname="bg-transparent text-[#4B4B4B]"
							trHeaderClassname="bg-white mb-4 rounded-md"
							tdClassname="px-0 bg-background border-b-8 border-white"
							containerClassname="border-separate border-spacing-y-4"
							isWhiteTable
							stickyHeader={true}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
